/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { useContext } from "react";
import {
  cancelPendingMeddreamReady,
  formatRegionUrl,
  HttpApiError,
  IErrorMessage,
  MeddreamWindowContext,
  waitForMeddreamReady,
} from "@advapacs/common";
import { getStudyMeddreamToken } from "../../pages/study/ducks/operators";
import { StudyAction } from "../../pages/study/ducks/studySlice";
import { useAppDispatch } from "../../config/store";
import { isSafari, isIOS } from "react-device-detect";
import { useFlag } from "@unleash/proxy-client-react";

// ============================= HOOK ==============================
const useOpenMeddream = (): ((studyIds: string[]) => Promise<void>) => {
  // ============================= CONTEXT ==============================

  const meddreamWindow = useContext(MeddreamWindowContext);
  const dispatch = useAppDispatch();
  const enabledMeddreamReadyListenerCleanup = useFlag(
    "meddream-ready-listener-cleanup"
  );

  // ============================= HELPERS ==============================

  const getUrl = (): string | undefined => {
    let region = "";

    if (!region) {
      // Region is resolved when the application first loads and cached in local storage.
      // There is no need to resolve it again.
      region = localStorage.getItem("region") ?? "";
    }

    if (!region) {
      // eslint-disable-next-line no-console
      console.log("Cannot find the region in the cache");
      return undefined;
    }
    const baseURL = formatRegionUrl(region, "meddream").replace(
      ".clinician",
      ""
    );

    return baseURL;
  };

  const openViewerWithNewWindow = (token: string) => {
    if (
      !meddreamWindow.meddreamWindow ||
      meddreamWindow.meddreamWindow.closed
    ) {
      // eslint-disable-next-line no-console
      console.log("[MEDDREAM]: Open window first time.");
      meddreamWindow.meddreamWindow = window.open(
        `${getUrl()}?token=${token}&add=true`,
        "MeddreamViewer"
      );
    } else {
      // eslint-disable-next-line no-console
      console.log(
        "[MEDDREAM]: Post message to MEDDREAM server. [ACTION]: REPLACE_STUDIES_WITH_TOKEN"
      );
      meddreamWindow.meddreamWindow.postMessage(
        {
          actionType: "REPLACE_STUDIES_WITH_TOKEN",
          actionData: {
            token: token,
          },
        },
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        getUrl()!
      );
    }
  };

  const redirectToNewUrl = (windowUrl: string) => {
    meddreamWindow.meddreamWindow.location.href = windowUrl;
  };

  // ============================= FUNCTIONS ==============================
  const openMeddream = (token: string): Promise<void> => {
    const baseURL = getUrl();

    if (!baseURL) return Promise.resolve();

    const finalUrl = `${baseURL}?token=${token}&add=true`;
    // eslint-disable-next-line no-console
    console.log("[MEDDREAM]: ", finalUrl);
    if (isSafari || isIOS) {
      redirectToNewUrl(finalUrl);
    } else {
      openViewerWithNewWindow(token);
    }

    return new Promise<void>((resolve) => {
      setTimeout(resolve, 500);
    });
  };

  const clearStudies = () => {
    // Post action message to MedDream Viewer
    // https://meddream.com/files/meddreamviewer/doc/MedDream-DICOM-Viewer-Communication-API.pdf
    const url = getUrl();
    if (url) {
      // eslint-disable-next-line no-console
      console.log("[MEDDREAM]: CLEAR STUDIES: " + url);
      meddreamWindow?.meddreamWindow?.postMessage(
        {
          actionType: "CLOSE_ALL_STUDIES",
        },
        url
      );
    }
  };

  const openEmptyWindow = (): Promise<void> => {
    if (!(isSafari || isIOS)) {
      // prevent open new empty window if browser is not safari or iOS
      // eslint-disable-next-line no-console
      console.log(
        "[MEDDREAM]: not allow open new empty window for when browser is not safari and not iOS device."
      );
      return Promise.resolve();
    }
    if (
      !meddreamWindow.meddreamWindow ||
      meddreamWindow.meddreamWindow?.closed
    ) {
      // OPEN NEW EMPTY WINDOW.
      meddreamWindow.meddreamWindow = window.open("", "MedDreamViewer");
    } else {
      // eslint-disable-next-line no-console
      console.log("[MEDDREAM WINDOW]: This window already opened.");
    }
    return new Promise<void>((resolve) => {
      setTimeout(resolve, 500);
    });
  };

  // ============================= ROOT ==============================

  return async (studyIds: string[]) => {
    try {
      await openEmptyWindow();
      const meddreamToken = await getStudyMeddreamToken(studyIds);
      const tokens: string[] = meddreamToken.data.tokens;
      clearStudies(); // clear current image before get new one.
      if (enabledMeddreamReadyListenerCleanup) {
        cancelPendingMeddreamReady();
      }
      if (tokens.length === 1) {
        await openMeddream(tokens[0]);
      } else if (tokens.length > 1) {
        // make sure meddream open first with async function to wait server ready
        await openMeddream(tokens[0]);
        const postAdditionalTokens = () => {
          // eslint-disable-next-line
          console.log("COMMUNICATION_SERVICE_READY");
          const tokensOpen = tokens.splice(1, tokens.length);
          tokensOpen.forEach((tk) => {
            const baseURL = getUrl();
            if (baseURL) {
              meddreamWindow.meddreamWindow.postMessage(
                {
                  actionType: "OPEN_STUDIES_WITH_TOKEN",
                  actionData: {
                    token: tk,
                  },
                },
                baseURL
              );
              meddreamWindow.meddreamWindow.focus();
            }
          });
        };
        if (enabledMeddreamReadyListenerCleanup) {
          waitForMeddreamReady(getUrl() ?? "", postAdditionalTokens);
        } else {
          window.addEventListener("message", ({ data, origin }) => {
            if (
              origin === getUrl() &&
              data.actionType === "COMMUNICATION_SERVICE_READY"
            ) {
              postAdditionalTokens();
            }
          });
        }
      }
    } catch (errorResponse) {
      const error = (errorResponse as IErrorMessage)?.response
        ?.data as HttpApiError;
      if (error.code === "1017") {
        dispatch(
          StudyAction.setShowPatientVerifySmsModal({
            show: true,
            studyIds,
          })
        );
      }
      console.error(error);
    }
  };
};

export default useOpenMeddream;
